import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import { StaticFileUrlService } from 'Angular/services/static-file-url.service';

/**
 * SVG Sprite Service
 *
 * Handles loading and inlining of SVG sprite files for improved performance.
 * The sprite is loaded once and cached, then can be referenced locally using #icon-name.
 */
@Injectable({
    providedIn: 'root',
})
export class SvgSpriteService {
    private spriteLoaded$ = new BehaviorSubject<boolean>(false);

    private spriteContent$: Observable<SafeHtml | null>;

    private spriteElementId = 'yc-svg-sprite-container';

    private inlineSpriteInitialized = false;

    constructor(
        private http: HttpClient,
        private sanitizer: DomSanitizer,
        private staticFileUrlService: StaticFileUrlService
    ) {
        // Load sprite on service initialization
        this.spriteContent$ = this.loadSprite();
    }

    /**
     * Load the SVG sprite file and prepare it for inlining
     */
    private loadSprite(): Observable<SafeHtml | null> {
        return this.staticFileUrlService.getAbsoluteURL('images/core/icons.svg').pipe(
            switchMap((url: string) => {
                // Remove any hash fragment from the URL
                const cleanUrl = url.split('#')[0];
                return this.http.get(cleanUrl, { responseType: 'text' });
            }),
            map((svgContent: string) => {
                // Sanitize the SVG content
                return this.sanitizer.bypassSecurityTrustHtml(svgContent);
            }),
            catchError(() => {
                // Failed to load SVG sprite - fall back to null
                return of(null);
            }),
            shareReplay(1) // Cache the result
        );
    }

    /**
     * Inject the SVG sprite into the DOM
     * This method is idempotent - multiple calls are safe
     */
    public inlineSprite(): void {
        if (this.inlineSpriteInitialized) {
            return;
        }
        this.inlineSpriteInitialized = true;

        const existingContainer = document.getElementById(this.spriteElementId);
        if (existingContainer?.querySelector('svg')) {
            this.spriteLoaded$.next(true);
            return;
        }

        this.spriteContent$.subscribe((safeContent: SafeHtml | null) => {
            if (safeContent) {
                const content = this.sanitizer.sanitize(1, safeContent);
                if (content) {
                    // Use existing container or create new one
                    let container = document.getElementById(this.spriteElementId);
                    if (!container) {
                        container = document.createElement('div');
                        container.id = this.spriteElementId;
                        container.style.display = 'none';
                        container.setAttribute('aria-hidden', 'true');
                        document.body.appendChild(container);
                    }
                    container.innerHTML = content;
                    this.spriteLoaded$.next(true);
                }
            }
        });
    }

    /**
     * Check if the sprite has been loaded and inlined
     */
    public isSpriteLoaded(): Observable<boolean> {
        return this.spriteLoaded$.asObservable();
    }

    /**
     * Get the inline reference for an icon
     * Returns just the hash reference without the full URL
     */
    public getInlineReference(iconName: string): string {
        return `#${iconName}`;
    }
}
